import React, { createContext, useContext, useEffect, useState } from "react";

type Theme = "dark" | "light" | "system";

/** What "system" actually resolved to. Never "system" itself. */
type ResolvedTheme = "dark" | "light";

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

type ThemeProviderState = {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
};

const initialState: ThemeProviderState = {
  theme: "system",
  resolvedTheme: "light",
  setTheme: () => null,
};

const resolveTheme = (theme: Theme): ResolvedTheme => {
  if (theme !== "system") return theme;
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

/**
 * Theme Provider for Lemonade brand theming with dark mode support.
 *
 * Uses class-based dark mode (compatible with Tailwind's `darkMode: "class"`).
 * Stores theme preference in localStorage and respects system preference.
 *
 * @example
 * ```tsx
 * import { ThemeProvider } from "@lemonadetraining/web-ui";
 *
 * function App() {
 *   return (
 *     <ThemeProvider defaultTheme="system" storageKey="lemonade-theme">
 *       <YourApp />
 *     </ThemeProvider>
 *   );
 * }
 * ```
 */
export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "lemonade-ui-theme",
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window === "undefined") {
      return defaultTheme;
    }
    const stored = localStorage.getItem(storageKey);
    const validThemes = ["dark", "light", "system"];
    const isValid = stored && validThemes.includes(stored);
    return isValid ? (stored as Theme) : defaultTheme;
  });

  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => resolveTheme(theme));

  useEffect(() => {
    const root = window.document.documentElement;
    const apply = (next: ResolvedTheme) => {
      root.classList.remove("light", "dark");
      root.classList.add(next);
      setResolvedTheme(next);
    };

    if (theme !== "system") {
      apply(theme);
      return undefined;
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const applySystemTheme = () => apply(mediaQuery.matches ? "dark" : "light");
    applySystemTheme();
    mediaQuery.addEventListener("change", applySystemTheme);
    return () => mediaQuery.removeEventListener("change", applySystemTheme);
  }, [theme]);

  const value = {
    theme,
    resolvedTheme,
    setTheme: (theme: Theme) => {
      localStorage.setItem(storageKey, theme);
      setTheme(theme);
    },
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

/**
 * Hook to access the current theme and setTheme function.
 *
 * @example
 * ```tsx
 * import { useTheme } from "@lemonadetraining/web-ui";
 *
 * function MyComponent() {
 *   const { theme, setTheme } = useTheme();
 *   return <button onClick={() => setTheme("dark")}>Dark Mode</button>;
 * }
 * ```
 */
export function useTheme() {
  const context = useContext(ThemeProviderContext);

  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }

  return context;
}
